import { useState, useEffect, useCallback, useRef } from "react";
import { Star, BadgeCheck, ChevronLeft, ChevronRight } from "lucide-react";
import { CITIES } from "@/cityConfig";
import ImageLightbox from "@/components/ImageLightbox";

const COPY = {
  ru: { badge: "Отзывы в других городах", subtitle: "Нам доверяют клиенты по всему миру" },
  en: { badge: "Reviews from other cities", subtitle: "Trusted by customers around the world" },
};

const LANG_META = {
  ru: "🇷🇺", en: "🇬🇧", tr: "🇹🇷", de: "🇩🇪", fr: "🇫🇷", es: "🇪🇸", it: "🇮🇹", ar: "🇸🇦", th: "🇹🇭",
};

const Stars = ({ value }) => (
  <div className="flex gap-0.5" aria-hidden="true">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`w-3.5 h-3.5 ${i <= value ? "fill-amber-400 text-amber-400" : "fill-gray-200 text-gray-200"}`} />
    ))}
  </div>
);

const cityName = (slug, lang) => {
  const c = CITIES[slug];
  if (!c) return slug;
  return lang === "en" ? c.nameEn : (lang === "tr" ? (c.nameTr || c.nameEn) : c.name);
};

const MarqueeCard = ({ r, lang, dark = false, onPhotoClick }) => {
  const initial = (r.name || "?").trim().charAt(0).toUpperCase();
  const origLang = (r.lang || "ru").slice(0, 2).toLowerCase();
  const translated = origLang !== lang ? (r.translations && r.translations[lang]) : "";
  const text = translated || r.text;
  const flag = LANG_META[origLang];
  const href = `/${r.city}/reviews${lang !== "ru" ? `?lang=${lang}` : ""}`;
  const alt = lang === "en" ? `Bouquet delivered to ${cityName(r.city, lang)}` : `Букет с доставкой — ${cityName(r.city, lang)}`;
  const cardCls = dark
    ? "bg-white/[0.04] border-white/10 hover:border-pink-400/40 hover:bg-white/[0.07]"
    : "bg-white border-gray-100 shadow-sm hover:shadow-lg hover:border-pink-200";
  return (
    <a
      href={href}
      className={`group w-[280px] sm:w-[320px] shrink-0 rounded-2xl border ${cardCls} hover:-translate-y-1 transition-all duration-300 flex flex-col cursor-pointer no-underline overflow-hidden self-start`}
      data-testid={`marquee-review-${r.id}`}
    >
      {r.photo_url && (
        <div
          className={`relative h-36 w-full overflow-hidden cursor-zoom-in ${dark ? "bg-white/5" : "bg-pink-50"}`}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPhotoClick && onPhotoClick({ src: r.photo_url, alt }); }}
          data-testid={`marquee-review-photo-${r.id}`}
        >
          <img
            src={r.photo_url}
            alt={alt}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
      )}
      <div className="p-5 flex flex-col">
      <div className="flex items-center gap-3 mb-2.5">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 text-white flex items-center justify-center font-bold shrink-0 text-sm">
          {initial}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className={`font-semibold text-sm truncate ${dark ? "text-white" : "text-gray-900"}`}>{r.name}</span>
            {flag && <span className="text-xs shrink-0" aria-hidden="true">{flag}</span>}
          </div>
          <div className={`text-[11px] font-medium truncate group-hover:underline ${dark ? "text-pink-400" : "text-pink-600"}`}>{cityName(r.city, lang)}</div>
        </div>
        <div className="ml-auto">
          <Stars value={r.rating} />
        </div>
      </div>
      <p className={`text-[13px] leading-relaxed ${dark ? "text-white/60" : "text-gray-600"} ${r.photo_url ? "line-clamp-3" : "line-clamp-4"}`}>{text}</p>
      <div className={`mt-3 flex items-center gap-1 text-[10px] font-medium ${dark ? "text-emerald-400" : "text-emerald-600"}`}>
        <BadgeCheck className="w-3 h-3" /> {lang === "en" ? "Verified review" : "Проверенный отзыв"}
      </div>
      </div>
    </a>
  );
};

const SPEED_PX_PER_SEC = 28;
const IDLE_RESUME_MS = 2500;

function useAutoScroll(enabled) {
  const ref = useRef(null);
  const pausedUntil = useRef(0);
  const hovering = useRef(false);
  const drag = useRef(null);

  const wrap = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const half = el.scrollWidth / 2;
    if (half <= 0) return;
    if (el.scrollLeft >= half) el.scrollLeft -= half;
    else if (el.scrollLeft <= 0) el.scrollLeft += half;
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    let raf = 0;
    let last = performance.now();
    let acc = 0;
    const tick = (now) => {
      const dt = Math.min(now - last, 100);
      last = now;
      if (!hovering.current && !drag.current && now > pausedUntil.current) {
        acc += (SPEED_PX_PER_SEC * dt) / 1000;
        if (acc >= 1) {
          el.scrollLeft += Math.floor(acc);
          acc -= Math.floor(acc);
          wrap();
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [enabled, wrap]);

  const pause = () => { pausedUntil.current = performance.now() + IDLE_RESUME_MS; };
  const scrollBy = (dir) => {
    const el = ref.current;
    if (!el) return;
    pause();
    const card = el.querySelector("a");
    const step = (card ? card.getBoundingClientRect().width : 320) + 20;
    const half = el.scrollWidth / 2;
    if (dir < 0 && el.scrollLeft < step) el.scrollLeft += half;
    else if (dir > 0 && el.scrollLeft > half - step) el.scrollLeft -= half;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
    setTimeout(wrap, 450);
  };
  const handlers = {
    onMouseEnter: () => { hovering.current = true; },
    onMouseLeave: () => { hovering.current = false; drag.current = null; },
    onTouchStart: pause,
    onTouchEnd: pause,
    onWheel: pause,
    onScroll: () => { if (!drag.current) wrap(); },
    onPointerDown: (e) => {
      if (e.pointerType !== "mouse") return;
      drag.current = { x: e.clientX, left: ref.current.scrollLeft, moved: false };
    },
    onPointerMove: (e) => {
      if (!drag.current || e.pointerType !== "mouse") return;
      const dx = e.clientX - drag.current.x;
      if (Math.abs(dx) > 4) drag.current.moved = true;
      ref.current.scrollLeft = drag.current.left - dx;
    },
    onPointerUp: () => { if (drag.current) { wrap(); pause(); } drag.current = null; },
    onClickCapture: (e) => { if (drag.current?.moved) { e.preventDefault(); e.stopPropagation(); } },
  };
  return { ref, handlers, scrollBy };
}

export default function ReviewsMarquee({ city = "", lang = "ru", dark = false }) {
  const l = lang === "en" ? "en" : "ru";
  const c = COPY[l];
  const [reviews, setReviews] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const { ref: trackRef, handlers, scrollBy } = useAutoScroll(reviews.length >= 3);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`/api/reviews?exclude_city=${encodeURIComponent(city)}&limit=24&lang=${lang}`);
      const d = await r.json();
      const list = (d.reviews || []).filter((x) => (x.text || "").trim().length > 20);
      // Reviews with a bouquet photo first — they build the most trust.
      list.sort((a, b) => (b.photo_url ? 1 : 0) - (a.photo_url ? 1 : 0));
      setReviews(list);
    } catch { /* keep empty */ }
  }, [city, lang]);

  useEffect(() => { load(); }, [load]);

  if (reviews.length < 3) return null;

  // Duplicate the list so the -50% translate loops seamlessly.
  const loop = [...reviews, ...reviews];

  const fade = dark ? "#0a0a0a" : "rgb(253 242 248 / 0.9)";

  return (
    <section
      className={`py-14 sm:py-20 overflow-hidden ${dark ? "bg-[#0a0a0a]" : "bg-gradient-to-b from-white to-pink-50/60"}`}
      data-testid="reviews-marquee-section"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center mb-8 sm:mb-10">
        <span className={`inline-block text-xs sm:text-sm font-semibold px-4 py-1.5 rounded-full mb-3 ${dark ? "bg-white/5 border border-white/10 text-pink-400" : "bg-white border border-pink-100 text-pink-600 shadow-sm"}`}>
          {c.badge}
        </span>
        <p className={`text-sm sm:text-base ${dark ? "text-white/40" : "text-gray-500"}`}>{c.subtitle}</p>
      </div>

      <div className="relative reviews-marquee group/marquee" data-testid="reviews-marquee">
        {/* edge fades */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-10 sm:w-28 z-10" style={{ background: `linear-gradient(to right, ${fade}, transparent)` }} />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-10 sm:w-28 z-10" style={{ background: `linear-gradient(to left, ${fade}, transparent)` }} />
        {/* manual arrows */}
        {[["prev", -1, ChevronLeft, "left-2 sm:left-5"], ["next", 1, ChevronRight, "right-2 sm:right-5"]].map(([k, dir, Icon, pos]) => (
          <button
            key={k}
            type="button"
            onClick={() => scrollBy(dir)}
            aria-label={k === "prev" ? (l === "en" ? "Previous reviews" : "Предыдущие отзывы") : (l === "en" ? "Next reviews" : "Следующие отзывы")}
            className={`absolute top-1/2 -translate-y-1/2 ${pos} z-20 w-10 h-10 sm:w-11 sm:h-11 rounded-full flex items-center justify-center border shadow-md transition-all hover:scale-105 active:scale-95 ${
              dark ? "bg-white/10 border-white/15 text-white hover:bg-white/20" : "bg-white/90 border-pink-100 text-gray-700 hover:text-pink-600 hover:bg-white"
            } backdrop-blur-sm`}
            data-testid={`reviews-marquee-${k}`}
          >
            <Icon className="w-5 h-5" />
          </button>
        ))}
        <div
          ref={trackRef}
          {...handlers}
          className="reviews-marquee-track gap-4 sm:gap-5 overflow-x-auto scrollbar-hide px-4 sm:px-6 cursor-grab active:cursor-grabbing select-none"
          style={{ scrollbarWidth: "none" }}
          data-testid="reviews-marquee-track"
        >
          {loop.map((r, i) => (
            <MarqueeCard key={`${r.id}-${i}`} r={r} lang={lang} dark={dark} onPhotoClick={setLightbox} />
          ))}
        </div>
      </div>
      <ImageLightbox src={lightbox?.src} alt={lightbox?.alt} onClose={() => setLightbox(null)} />
    </section>
  );
}
